import { useCallback, useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { MultiSelectCheckbox } from "@/components/ui/multi-select-checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import {
  BalanceAdjustmentAuditRow,
  BalanceAdjustmentType,
  getBalanceAdjustmentAudits,
} from "@/Api/balanceAdjustments";
import { FiltersCard } from "./FiltersCard";
import { TablePagination } from "./TablePagination";
import { ReferenceDataProps, toOptions } from "../types";

const TYPE_LABELS: Record<BalanceAdjustmentType, string> = {
  seller: "Vendedor",
  unity: "Unidade",
  contest_product: "Concurso/Bolão",
  leftover_product: "Sobras",
};

const TYPE_OPTIONS = Object.entries(TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const formatDateTime = (value: string): string => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("pt-BR");
};

interface AuditFilters {
  balanceTypes: string[];
  contestIds: string[];
  unityIds: string[];
}

const EMPTY_FILTERS: AuditFilters = {
  balanceTypes: [],
  contestIds: [],
  unityIds: [],
};

/**
 * Histórico de auditoria das correções manuais de balanço.
 *
 * Os selects apenas montam a seleção; a listagem só é consultada ao clicar em
 * Filtrar, Atualizar ou ao trocar de página.
 */
export const AuditsTab = ({ contests, unities }: ReferenceDataProps) => {
  const [rows, setRows] = useState<BalanceAdjustmentAuditRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });

  const [draftFilters, setDraftFilters] = useState<AuditFilters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<AuditFilters>(EMPTY_FILTERS);

  const isDirty = draftFilters !== appliedFilters;
  const appliedCount = Object.values(appliedFilters).filter(
    (values) => values.length > 0
  ).length;

  const loadRows = useCallback(async () => {
    setIsLoading(true);

    const response = await getBalanceAdjustmentAudits(page, {
      balance_type: appliedFilters.balanceTypes as BalanceAdjustmentType[],
      contest_id: appliedFilters.contestIds,
      unity_id: appliedFilters.unityIds,
    });

    setIsLoading(false);

    if (!response.ok) return;

    const paginator = response.data?.response;
    setRows(paginator?.data ?? []);
    setPagination({
      current_page: paginator?.current_page ?? 1,
      last_page: paginator?.last_page ?? 1,
      total: paginator?.total ?? 0,
    });
  }, [page, appliedFilters]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const handleFilterChange =
    (key: keyof AuditFilters) => (values: string[]) => {
      setDraftFilters((current) => ({ ...current, [key]: values }));
    };

  const handleApplyFilters = () => {
    setAppliedFilters(draftFilters);
    setPage(1);
  };

  const handleClearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
  };

  return (
    <div className="flex flex-col gap-4">
      <FiltersCard
        appliedCount={appliedCount}
        isDirty={isDirty}
        isLoading={isLoading}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        onRefresh={loadRows}
      >
        <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-1">
            <Label>Tipo de balanço</Label>
            <MultiSelectCheckbox
              options={TYPE_OPTIONS}
              selectedValues={draftFilters.balanceTypes}
              hideBadge
              onSelectionChange={handleFilterChange("balanceTypes")}
              placeholder="Todos os tipos"
            />
          </div>

          <div className="space-y-1">
            <Label>Concurso</Label>
            <MultiSelectCheckbox
              options={toOptions(contests)}
              selectedValues={draftFilters.contestIds}
              hideBadge
              onSelectionChange={handleFilterChange("contestIds")}
              placeholder="Todos os concursos"
              searchable
              searchPlaceholder="Buscar concurso..."
            />
          </div>

          <div className="space-y-1">
            <Label>Unidade</Label>
            <MultiSelectCheckbox
              options={toOptions(unities)}
              selectedValues={draftFilters.unityIds}
              hideBadge
              onSelectionChange={handleFilterChange("unityIds")}
              placeholder="Todas as unidades"
              searchable
              searchPlaceholder="Buscar unidade..."
            />
          </div>
        </div>
      </FiltersCard>

      <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data/Hora</TableHead>
              <TableHead>Usuário</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Registro</TableHead>
              <TableHead className="text-right">Balanço</TableHead>
              <TableHead className="text-right">Valor preso</TableHead>
              <TableHead>Motivo</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center">
                  <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                </TableCell>
              </TableRow>
            )}

            {!isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                  Nenhuma correção registrada.
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              rows.map((row) => (
                <TableRow key={row.uuid}>
                  <TableCell className="whitespace-nowrap">
                    {formatDateTime(row.created_at)}
                  </TableCell>
                  <TableCell>{row.user_name ?? "-"}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {TYPE_LABELS[row.balance_type] ?? row.balance_type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col text-xs">
                      <span>{row.seller_name ?? row.unity_name ?? "-"}</span>
                      <span className="text-muted-foreground">
                        {[row.product_name, row.contest_name].filter(Boolean).join(" • ")}
                      </span>
                      <span className="text-muted-foreground">
                        {row.balance_table} #{row.balance_record_id}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <span className="line-through text-muted-foreground">
                      {row.previous_balance}
                    </span>{" "}
                    → <span className="font-semibold">{row.new_balance}</span>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <span className="line-through text-muted-foreground">
                      {row.previous_with_held_balance}
                    </span>{" "}
                    → <span className="font-semibold">{row.new_with_held_balance}</span>
                  </TableCell>
                  <TableCell className="max-w-[240px] text-xs">{row.reason ?? "-"}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      <TablePagination
        currentPage={pagination.current_page}
        lastPage={pagination.last_page}
        total={pagination.total}
        onChange={setPage}
      />
    </div>
  );
};
